/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from 'react';
import '../styles.css';
import { Form } from '@unform/web';
import { useHistory } from 'react-router-dom';
import { addMonths, format, parse, formatISO } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { ProposalSplitType } from 'src/enums';
import { LoaderIcon } from 'src/components/Loader';
import api from 'src/services/api';
import BackButton from 'src/components/BackButton';
import { useAuth } from 'src/hooks/auth';
import { ensureLocaleDate } from 'src/utils/dates';
import {
  Container,
  Bottom,
  Header,
  Left,
  NewContractContent,
  Top,
  Body,
  ProposalTypePickerContainer,
  ProposalTypeDescriptionContainer,
  ProposalDescriptionContainer,
  MiddleLine,
} from '../styles';
import PercentageInput from '../components/PercentageInput';
import DateInput from '../components/DateInput';
import MerchantInput from '../components/MerchantInput';
import TotalValueInput from '../components/TotalValueInput';
import DescriptionInput from '../components/DescriptionInput';
import Breadcrumb from '../components/BreadCrumb';
import { DataServerDTO } from '../utils/interfaces';
import { splitPaths, ProposalSplitTypeDescription } from '../utils/const';

const NewContract: React.FC = () => {
  const [startDueDate, setStartDate] = useState<any>(addMonths(new Date(), 1));
  const [dueDate, setDueDate] = useState<string>(
    format(startDueDate, 'dd/MM/yyyy'),
  );
  const [startValidFromDate, setStartValidFromDate] = useState<any>(new Date());
  const [validFromDate, setValidFromDate] = useState<string>(
    format(startValidFromDate, 'dd/MM/yyyy'),
  );
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [percentage, setPercentage] = useState<string>();
  const [totalValue, setTotalValue] = useState<string>();
  const [dataEdit, setDataEdit] = useState<DataServerDTO>();
  const [edition, setEdition] = useState(false);
  const [selectedProposalType, setSelectedProposalType] = useState(
    ProposalSplitType.free,
  );
  const [description, setDescription] = useState<string>('');
  const [showValidFromCalendar, setShowValidFromCalendar] = useState(false);
  const [showDueDateCalendar, setShowDueDateCalendar] = useState(false);
  const { selectedDocument } = useAuth();

  useEffect(() => {
    const rawProposalInfo = localStorage.getItem('proposalInfoClient');
    if (rawProposalInfo) {
      const proposalInfo = JSON.parse(rawProposalInfo);
      setDataEdit(proposalInfo);
      const proposalPercentage = proposalInfo.percentage_value.toLocaleString(
        'pt-BR',
      );
      setPercentage(proposalPercentage);
      const parsedTotalValue = parseFloat(
        proposalInfo.total_value,
      ).toLocaleString('pt-BR', { useGrouping: false });
      const proposalTotalValue = proposalInfo.total_value
        ? parsedTotalValue
        : undefined;

      const validFrom = proposalInfo.valid_from
        ? new Date(proposalInfo.valid_from)
        : new Date();

      setTotalValue(proposalTotalValue);
      setDueDate(
        format(new Date(ensureLocaleDate(proposalInfo.due_date)), 'dd/MM/yyyy'),
      );
      setValidFromDate(format(validFrom, 'dd/MM/yyyy'));
      setSelectedProposalType(proposalInfo.split_type as ProposalSplitType);
      setEdition(true);
      setDescription(proposalInfo.description || '');
    } else {
      setDataEdit(undefined);
      setEdition(false);
    }
    setLoadingData(false);
  }, []);

  async function handleSubmit(data: DataServerDTO) {
    setLoading(true);
    const parsedDueDate = parse(dueDate, 'dd/MM/yyyy', new Date());
    const formatedDueDate = formatISO(parsedDueDate, {
      representation: 'date',
    });

    const parsedValidFromDate = parse(validFromDate, 'dd/MM/yyyy', new Date());
    const now = new Date();
    const todayUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );

    const isValidFromToday =
      parsedValidFromDate.getUTCFullYear() === todayUTC.getUTCFullYear() &&
      parsedValidFromDate.getUTCMonth() === todayUTC.getUTCMonth() &&
      parsedValidFromDate.getUTCDate() === todayUTC.getUTCDate();

    let validFromValue = null;
    if (!isValidFromToday) {
      // Format as yyyy-mm-ddT00:00:00Z
      validFromValue = `${formatISO(parsedValidFromDate, {
        representation: 'date',
      })}T00:00:00`;
    }

    const formatNumberStringToUseDot = (numberString: string) =>
      numberString.replace(',', '.');

    const requestPayload = {
      debtor_document: data.debtor.document,
      percentage_value: percentage && formatNumberStringToUseDot(percentage),
      due_date: formatedDueDate,
      total_value: totalValue ? formatNumberStringToUseDot(totalValue) : null,
      split_type: selectedProposalType,
      description,
      valid_from: validFromValue,
    };

    if (data) {
      try {
        let response;
        if (edition) {
          response = await api.patch(
            `${process.env.REACT_APP_API_REQUEST_ENDPOINT}/api/v1/merchants/${selectedDocument}/proposals/${dataEdit?.id}`,
            requestPayload,
          );
        } else {
          response = await api.post(
            `${process.env.REACT_APP_API_REQUEST_ENDPOINT}/api/v1/merchants/${selectedDocument}/proposals`,
            requestPayload,
          );
        }

        localStorage.setItem(
          'proposalInfoClient',
          JSON.stringify(response.data),
        );
        history.push('/newcontract/review');
      } finally {
        setLoading(false);
      }
    }
  }

  const renderTypePicker = useCallback(
    () => (
      <ProposalTypePickerContainer>
        <button
          type="button"
          className={
            selectedProposalType === ProposalSplitType.free ? 'selected' : ''
          }
          onClick={() => {
            setSelectedProposalType(ProposalSplitType.free);
            setTotalValue(undefined);
          }}
        >
          Livre
        </button>
        <button
          type="button"
          className={
            selectedProposalType === ProposalSplitType.spot ? 'selected' : ''
          }
          onClick={() => setSelectedProposalType(ProposalSplitType.spot)}
        >
          Pontual
        </button>
        <button
          type="button"
          className={
            selectedProposalType === ProposalSplitType.monthly ? 'selected' : ''
          }
          onClick={() => setSelectedProposalType(ProposalSplitType.monthly)}
        >
          Mensal
        </button>
      </ProposalTypePickerContainer>
    ),
    [selectedProposalType],
  );

  const renderProposalInputs = () => {
    const typesWithTotalValue = [
      ProposalSplitType.spot,
      ProposalSplitType.monthly,
    ];
    const shouldRenderTotalValue = typesWithTotalValue.includes(
      selectedProposalType,
    );
    const totalValueProps =
      selectedProposalType === ProposalSplitType.monthly
        ? { label: 'Defina o valor mensal da proposta' }
        : {};

    return (
      <>
        <Top>
          <Left>
            <DateInput
              loadingData={loadingData}
              date={validFromDate}
              setDate={setValidFromDate}
              startDate={startValidFromDate}
              setStartDate={setStartValidFromDate}
              showCalendar={showValidFromCalendar}
              setShowCalendar={setShowValidFromCalendar}
              groupLabel="Inicío"
              label="Data de início da Proposta"
            />
          </Left>
        </Top>
        <MiddleLine>
          <Left>
            <MerchantInput
              loadingData={loadingData}
              document={dataEdit?.debtor.document}
            />
            <PercentageInput
              loadingData={false}
              value={percentage}
              onChangePercentage={setPercentage}
            />
            <DateInput
              loadingData={loadingData}
              date={dueDate}
              setDate={setDueDate}
              startDate={startDueDate}
              setStartDate={setStartDate}
              showCalendar={showDueDateCalendar}
              setShowCalendar={setShowDueDateCalendar}
              groupLabel="Limites"
              label="Data limite da Proposta"
            />
            {shouldRenderTotalValue && (
              <TotalValueInput
                loadingData={loadingData}
                setTotalValue={setTotalValue}
                totalValue={totalValue}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...totalValueProps}
              />
            )}
          </Left>
        </MiddleLine>
        <Bottom>
          <DescriptionInput
            loadingData={loadingData}
            description={description}
            setDescription={setDescription}
          />
        </Bottom>
      </>
    );
  };

  return (
    <Container>
      <NewContractContent>
        <Header>
          <Breadcrumb paths={splitPaths} />
          <BackButton address="/proposals" />
        </Header>
        <Body>
          <Form onSubmit={handleSubmit}>
            <ProposalDescriptionContainer>
              <h1>Proposta de Split</h1>
            </ProposalDescriptionContainer>
            {renderTypePicker()}
            <ProposalTypeDescriptionContainer>
              <h1>
                {ProposalSplitTypeDescription[selectedProposalType].title}
              </h1>
              <p>
                {ProposalSplitTypeDescription[selectedProposalType].description}
              </p>
            </ProposalTypeDescriptionContainer>
            {renderProposalInputs()}
            <br />
            {!loading ? (
              <Bottom>
                <button type="submit">Continuar</button>
              </Bottom>
            ) : (
              <Bottom>
                <LoaderIcon />
              </Bottom>
            )}
          </Form>
        </Body>
      </NewContractContent>
    </Container>
  );
};

export default NewContract;
